import { useMemo, useState } from "react";
import { Search, X, Loader2, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileTypeIcon } from "./FileTypeIcon";
import {
  formatFileSize,
  useLinkExistingMedia,
  useMediaList,
  type LinkExistingTargetType,
} from "../hooks/useMedia";

type ContextScope = {
  companyId?: string;
  productId?: string;
  projectId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Module record we're linking files to. */
  target: {
    type: LinkExistingTargetType;
    id: string;
    label?: string | null;
  };
  /** IDs already linked to this record (hidden from the picker). */
  excludeIds?: string[];
  /** Optional pre-filter to surface contextually relevant files first
   *  (e.g. files already on the same product / project / company). */
  context?: ContextScope;
  /** Called after a successful link operation. */
  onLinked?: (count: number) => void;
};

export function LinkExistingMediaModal({
  open,
  onClose,
  target,
  excludeIds = [],
  context,
  onLinked,
}: Props) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const link = useLinkExistingMedia();

  // Page through enough rows that the user can find what they want without
  // an infinite-scroll implementation; the search input narrows further.
  const { data, isLoading } = useMediaList({
    search: search.trim() || undefined,
    companyId: context?.companyId,
    productId: context?.productId,
    projectId: context?.projectId,
    limit: 50,
  });

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);
  const visible = useMemo(
    () => (data?.data ?? []).filter((f) => !excludeSet.has(f.id)),
    [data, excludeSet],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (selected.size === 0) return;
    await link.mutateAsync({
      fileIds: Array.from(selected),
      target,
    });
    onLinked?.(selected.size);
    setSelected(new Set());
    setSearch("");
    onClose();
  }

  function cancel() {
    setSelected(new Set());
    setSearch("");
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && cancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Link from Media Vault</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Media Vault…"
              className="pl-9"
            />
          </div>

          <div className="max-h-[50vh] overflow-y-auto rounded border">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            ) : visible.length === 0 ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                {search.trim()
                  ? "No matching files."
                  : "No Media Vault files available to link."}
              </div>
            ) : (
              <ul className="divide-y">
                {visible.map((f) => {
                  const isSelected = selected.has(f.id);
                  const display = f.title || f.originalName;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        onClick={() => toggle(f.id)}
                        className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                          isSelected ? "bg-primary/5" : ""
                        }`}
                      >
                        <FileTypeIcon fileType={f.fileType} size={24} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{display}</div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {f.code} · {formatFileSize(f.fileSize)}
                            {f.originalName && f.originalName !== display
                              ? ` · ${f.originalName}`
                              : ""}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {selected.size > 0
                ? `${selected.size} file${selected.size === 1 ? "" : "s"} selected`
                : "Pick one or more files to link"}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={cancel}>
                <X size={12} /> Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={selected.size === 0 || link.isPending}
                onClick={submit}
              >
                {link.isPending && (
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                )}
                Link Selected
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
